import { environment } from '../../../environments/environment'
import { PREFIJO_S3_KEY } from './subidor-s3'
import type { BackendStorage } from '../offline/db'

/**
 * Rutas deterministas (sin timestamp) de cada foto en el bucket 'fotos-telas'
 * — un único lugar para armarlas, usado tanto al subir (sincronizador.ts)
 * como al borrar (eliminador.ts). Antes cada uno armaba el string por su
 * cuenta con template literals repetidos; si algún día cambia el esquema de
 * rutas y se actualiza uno sin el otro, un borrado dejaría huérfanos (o peor,
 * borraría la ruta equivocada) sin que nada lo avise. Ver
 * docs/plan-eliminar-proveedor-producto-tela-2026-09-05.md.
 */

export function rutaTarjetaProveedor(codigoProveedor: string): string {
  return `${codigoProveedor}/tarjeta.jpg`
}

export function rutaColorEstructuraProducto(productoId: string): string {
  return `${productoId}/color-estructura.jpg`
}

export function rutaFotoGaleriaProducto(productoId: string, fotoId: string): string {
  return `${productoId}/foto-${fotoId}.jpg`
}

export function rutaTelaFrente(productoId: string, telaId: string): string {
  return `${productoId}/${telaId}-frente.jpg`
}

export function rutaTelaReverso(productoId: string, telaId: string): string {
  return `${productoId}/${telaId}-reverso.jpg`
}

// URL pública real de Supabase Storage, con la forma
// `${supabaseUrl}/storage/v1/object/public/fotos-telas/<ruta>` — necesitamos
// reconocerla para poder extraer la ruta relativa al bucket (lo que
// `SubidorSupabase.borrar()` espera), igual que ya reconocemos `s3key:` para S3.
const PREFIJO_URL_SUPABASE = `${environment.supabaseUrl}/storage/v1/object/public/fotos-telas/`

/**
 * Determina en qué backend vive de verdad una foto ya subida, a partir de la
 * URL/key que quedó guardada localmente (2026-09-07, feature de borrado en
 * cascada con bucket S3 privado).
 *
 * Devuelve `undefined` si no hay nada guardado (la foto nunca se subió — no
 * hay nada que borrar) o si la URL no calza con ningún patrón conocido (caso
 * inesperado; preferimos no intentar un borrado a ciegas a arriesgarnos a
 * mandar una ruta mal armada a cualquiera de los dos storages).
 */
export function backendDeUrl(url: string | undefined): { ruta: string; backend: BackendStorage } | undefined {
  if (!url) return undefined
  if (url.startsWith(PREFIJO_S3_KEY)) {
    return { ruta: url.slice(PREFIJO_S3_KEY.length), backend: 's3' }
  }
  if (url.startsWith(PREFIJO_URL_SUPABASE)) {
    return { ruta: url.slice(PREFIJO_URL_SUPABASE.length), backend: 'supabase' }
  }
  console.warn(`[rutas-storage] No se pudo determinar el backend de almacenamiento de "${url}" — no se va a intentar borrar.`)
  return undefined
}
